import CheckIcon from '@mui/icons-material/Check';
import { createReactExtension } from '@backstage/core-plugin-api';
import { homePlugin } from '@backstage/plugin-home';
import { ListPickerSettingsDialog } from '../listPickerSettingsDialog/ListPickerSettingsDialog';
import { NoContentSelected } from '../noContentSelected/NoContentSelected';
import { NoContentType } from '../noContentSelected/types/NoContentType.enum';
import { Typography } from '@material-ui/core';
import { useStyles } from './scoreCards.style';
import { useTeamData } from './hooks/useTeamData';

import { CustomCard, useTeamList, useUserTeams } from '@internal/ui-common';
import {
  getScoreCardsHomePage,
  ScoreCardHomePage,
} from './cards/scoreCardsHomePage';
import { HorizontalScrollGrid, Progress } from '@backstage/core-components';
import { useEffect, useMemo, useRef } from 'react';

const schemaForSettings = {
  title: 'Select the desired team',
  type: 'object',
  properties: {
    selectedTeam: {
      type: 'string',
      title: 'Teams',
      enum: ['Loading teams ...'],
    },
  },
};

function shuffleArray<T>(array: T[]): T[] {
  return array.sort(() => Math.random() - 0.5);
}

function ScoreCards({ selectedTeam }: any) {
  const { cx, classes } = useStyles();
  const { teams: allTeams } = useTeamList();
  const { teams: teamsUserIsPartOf } = useUserTeams();

  const teamToShow =
    allTeams.find(team => team.name === selectedTeam) ?? teamsUserIsPartOf[0];
  const teamNames = useMemo(() => allTeams.map(team => team.name), [allTeams]);
  const teamData = useTeamData(teamToShow);

  // useref is still in place because the async api of renovate, vulnerabilities and the other scorecard loading state is incorrect
  // because of that, the rendering happens twice instead of only once (in useMemo).
  const initialScoreCards = useRef<Array<ScoreCardHomePage<any>> | null>(null);

  const scoreCards = useMemo(() => {
    if (teamData?.isLoading || !teamToShow) return [];

    const allCards: Array<ScoreCardHomePage<any>> = getScoreCardsHomePage(
      teamToShow,
      teamData.renovateData,
      teamData.vulnerabilitiesData,
      teamData.secretsData,
      teamData.longestOutdatedSecret,
    );
    const validCards = allCards.filter(
      card =>
        card.props.score?.red > 0 ||
        card.props.score?.red?.fix > 0 ||
        card.props.score?.orange > 0 ||
        card.props.score?.orange?.fix > 0,
    );

    initialScoreCards.current = shuffleArray(validCards);
    return initialScoreCards.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teamToShow?.name,
    teamData.renovateData,
    teamData.vulnerabilitiesData,
    teamData.secretsData,
    teamData.longestOutdatedSecret,
  ]);

  useEffect(() => {
    if (teamNames.length > 0) {
      schemaForSettings.properties.selectedTeam.enum = teamNames;
    }
  }, [teamNames]);

  if (teamData?.isLoading) return <Progress />;

  if (scoreCards.length > 0) {
    return (
      <section className={cx(classes.scoreCards)}>
        <HorizontalScrollGrid>
          {scoreCards.map((scorecard, index) => {
            const { Component, props, title, tooltip, deepLinkTitle } =
              scorecard;
            return (
              <div key={index} style={{ height: '100%', minWidth: '15rem' }}>
                <Component
                  title={title}
                  tooltip={tooltip}
                  deepLinkTitle={deepLinkTitle}
                  {...props}
                />
              </div>
            );
          })}
        </HorizontalScrollGrid>
      </section>
    );
  }

  return (
    <CustomCard title={`Scorecards ${teamToShow?.name}`}>
      {teamToShow ? (
        <article className={cx(classes.allGood)}>
          <CheckIcon />
          <Typography variant="body1" className={cx(classes.text)}>
            All systems are optimized and secure.
            <br />
            Everything is running smoothly.
          </Typography>
        </article>
      ) : (
        <NoContentSelected type={NoContentType.Team} />
      )}
    </CustomCard>
  );
}

export { ScoreCards };

export const ScoreCardsWidget = homePlugin.provide(
  createReactExtension({
    name: 'Scorecards',
    component: {
      lazy: () => import('./ScoreCards').then(m => m.ScoreCards),
    },
    data: {
      title: 'Scorecards',
      description:
        'Indicates if action is needed around Renovate, secrets, or vulnerabilities.',
      'home.widget.config': {
        layout: {
          width: { defaultColumns: 6, minColumns: 3 },
          height: { defaultRows: 5, minRows: 5, maxRows: 5 },
        },
        settings: {
          schema: schemaForSettings,
          uiSchema: {
            selectedTeam: { 'ui:widget': ListPickerSettingsDialog },
          },
        },
      },
    },
  }),
);
